// Admin console endpoint bindings (mirror backend /admin/* and /invites/*).
// All require an admin JWT — the backend enforces `require_admin`.
import { api } from "./client";

export interface AdminUser {
  id: string;
  email: string;
  display_name: string | null;
  account_tier: string;
  is_admin: boolean;
  created_at: string;
}

export interface InviteCreateResponse {
  token: string;
  link: string;
  grants_tier: string;
  expires_at: string;
}

export interface InviteListItem {
  id: string;
  grants_tier: string;
  created_at: string;
  expires_at: string;
  used_at: string | null;
  used_by_email: string | null;
  status: "used" | "expired" | "outstanding";
}

export function listUsers(): Promise<AdminUser[]> {
  return api.get<AdminUser[]>("/admin/users");
}

export function setUserTier(userId: string, accountTier: string): Promise<AdminUser> {
  return api.patch<AdminUser>(`/admin/users/${encodeURIComponent(userId)}/tier`, {
    account_tier: accountTier,
  });
}

export function createInvite(opts?: {
  grants_tier?: string;
  expires_in_days?: number;
}): Promise<InviteCreateResponse> {
  return api.post<InviteCreateResponse>("/invites/", opts ?? {});
}

export function listInvites(): Promise<InviteListItem[]> {
  return api.get<InviteListItem[]>("/invites/");
}

export function deleteInvite(id: string): Promise<void> {
  return api.delete<void>(`/invites/${encodeURIComponent(id)}`);
}

// ── Field engines (Projects specialization catalog) ──────────────────────────
export type FieldEngineStatus = "auto" | "reviewed" | "pinned";

export interface FieldEngine {
  path: string;
  label: string | null;
  origin: string; // curated | generated | specialist
  status: FieldEngineStatus;
  uses: number;
  has_deep: boolean; // carries full per-strategy prompt overrides (specialist-grade)
  candidate: boolean; // generated + heavily used → worth promoting/deepening
  created_at: string;
}

export function listFieldEngines(): Promise<FieldEngine[]> {
  return api.get<FieldEngine[]>("/agents/fields");
}

export function setFieldEngineStatus(
  path: string,
  status: FieldEngineStatus,
): Promise<FieldEngine> {
  // `path` is a slash-delimited taxonomy path; the route is {path:path}, so the
  // slashes must stay literal (don't encodeURIComponent the whole thing).
  return api.patch<FieldEngine>(`/agents/fields/${path}`, { status });
}

export function deepenFieldEngine(path: string): Promise<FieldEngine> {
  return api.post<FieldEngine>(`/agents/fields/${path}/deepen`, {});
}
